import React, { useState } from "react";
import { useFormik } from "formik";
import Swal from "sweetalert2";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import Breadcrums from "../../common/breadcrumbs";
import {
    globalLoader,
    handleServerValidations,
} from "../../../../utils/commonfunction";
import { SWAL_SETTINGS } from "../../../../utils/Constants";
import { AddCategory } from "../../services/appMenu.services";

const AddAppMenuCategory = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const [submitted, setSubmitted] = useState(false);
    const [previewImage, setPreviewImage] = useState(null);

    const breadcrumbs = [
        { title: t("link_dashboard"), url: "/admin/dashboard" },
        { title: "App Menu Categories", url: "/admin/app-menu-category/list" },
        { title: t("link_add"), url: "" },
    ];

    const validationSchema = Yup.object().shape({
        title: Yup.string().trim().required("Title is required"),
        type: Yup.string().required("Type is required"),
    });

    const formik = useFormik({
        initialValues: {
            title: "",
            image: null,
            status: 1,
            type: "Free",
            sort_order: 0,
        },
        validationSchema: validationSchema,
        onSubmit: (values) => {
            globalLoader(true);
            setSubmitted(true);

            const formData = new FormData();
            formData.append("title", values.title);
            formData.append("status", values.status);
            formData.append("type", values.type);
            formData.append("sort_order", values.sort_order);
            if (values.image) {
                formData.append("image", values.image);
            }

            AddCategory(formData)
                .then((response) => {
                    if (response.success) {
                        Swal.fire({
                            icon: "success",
                            text: response.message,
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        setTimeout(() => {
                            navigate(`/admin/app-menu-category/list`);
                        }, 1000);
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(response),
                            ...SWAL_SETTINGS,
                        });
                        globalLoader(false);
                        setSubmitted(false);
                    }
                })
                .catch((error) => {
                    console.log("error ====> ", error);
                    globalLoader(false);
                    setSubmitted(false);
                });
        },
    });

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            formik.setFieldValue("image", file);
            const reader = new FileReader();
            reader.onload = (ev) => setPreviewImage(ev.target.result);
            reader.readAsDataURL(file);
        }
    };

    const handleTitleChange = (e) => {
        const title = e.target.value;
        formik.setFieldValue("title", title);
    };

    return (
        <>
            <Breadcrums data={breadcrumbs} />
            <div className="animation_fade">
                <div className="card custom-card">
                    <div className="card-body">
                        <h6 className="main-content-label mb-3">Add Category</h6>
                        <form onSubmit={formik.handleSubmit}>
                            <div className="row row-sm">
                                {/* Title */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="title" className="text-left d-flex">
                                        Title :<span className="requirestar">*</span>
                                    </label>
                                    <input
                                        name="title"
                                        id="title"
                                        type="text"
                                        className="form-control"
                                        placeholder="Enter category title"
                                        onChange={handleTitleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.title}
                                    />
                                    {formik.errors.title && formik.touched.title && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.title}
                                        </span>
                                    )}
                                </div>

                                {/* Status */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="status" className="text-left d-flex">
                                        Status :
                                    </label>
                                    <select
                                        name="status"
                                        id="status"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.status}
                                    >
                                        <option value={1}>{t("Active")}</option>
                                        <option value={0}>{t("Inactive")}</option>
                                    </select>
                                </div>

                                {/* Type */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="type" className="text-left d-flex">
                                        Type :<span className="requirestar">*</span>
                                    </label>
                                    <select
                                        name="type"
                                        id="type"
                                        className="form-control"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.type}
                                    >
                                        <option value="Free">Free</option>
                                        <option value="Plus">Plus</option>
                                    </select>
                                    {formik.errors.type && formik.touched.type && (
                                        <span className="text-danger d-flex text-left">
                                            {formik.errors.type}
                                        </span>
                                    )}
                                </div>

                                {/* Sort Order */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="sort_order" className="text-left d-flex">
                                        Sort Order :
                                    </label>
                                    <input
                                        name="sort_order"
                                        id="sort_order"
                                        type="number"
                                        min="0"
                                        className="form-control"
                                        placeholder="Enter sort order (e.g. 1, 2, 3)"
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.sort_order}
                                    />
                                </div>

                                {/* Image */}
                                <div className="col-lg-6 form-group">
                                    <label htmlFor="image" className="text-left d-flex">
                                        Icon / Image :
                                    </label>
                                    <input
                                        name="image"
                                        id="image"
                                        type="file"
                                        className="form-control"
                                        accept="image/*"
                                        onChange={handleImageChange}
                                    />
                                    {previewImage && (
                                        <div className="mt-2">
                                            <img
                                                src={previewImage}
                                                alt="Preview"
                                                style={{
                                                    width: "80px",
                                                    height: "80px",
                                                    objectFit: "cover",
                                                    borderRadius: "10px",
                                                    border: "1px solid #dee2e6",
                                                }}
                                            />
                                        </div>
                                    )}
                                </div>

                                <div className="col-md-12 mt-3">
                                    <button
                                        className="btn btn-main-primary signbtn mr-2"
                                        type="submit"
                                        disabled={submitted}
                                    >
                                        <i className="ace-icon fa fa-check bigger-110 mx-1"></i>
                                        {t("btn_submit")}
                                    </button>
                                    <button
                                        className="btn ripple btn-secondary"
                                        type="button"
                                        onClick={() => navigate(-1)}
                                    >
                                        <i className="ace-icon fa fa-times bigger-110 mx-1"></i>
                                        {t("btn_cancel")}
                                    </button>
                                </div>
                            </div>
                        </form>
                    </div>
                </div>
            </div>
        </>
    );
};

export default AddAppMenuCategory;
